import React, { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseconfig';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];
const RADIAN = Math.PI / 180;

const SubareaBudgetVisualization = ({ userArea }) => {
  const [budgetData, setBudgetData] = useState(null);
  const [chartSize, setChartSize] = useState({ width: 300, height: 300 });
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        const size = Math.min(width, 400);
        setChartSize({ width: size, height: size });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const fetchBudgetData = async () => {
      if (!userArea) {
        console.error("No area/subarea selected");
        setError("No se ha seleccionado ninguna área o subárea");
        return;
      }

      try {
        console.log("Fetching data for area/subarea:", userArea);
        
        const budgetDocRef = doc(db, 'budgets', userArea);
        const budgetDocSnap = await getDoc(budgetDocRef);
        
        if (!budgetDocSnap.exists()) {
          console.error("Area/Subarea not found in budgets collection");
          setError("No se encontró información para el área o subárea seleccionada");
          return;
        }

        const budgetInfo = budgetDocSnap.data();
        console.log("Budget info:", budgetInfo);

        const worksCollection = collection(db, userArea);
        const worksSnapshot = await getDocs(worksCollection);
        const works = worksSnapshot.docs
          .map(doc => {
            const data = doc.data();
            // Ensure we're not including any fields named 'metadata' or with undefined values
            if (data.obra && data.obra !== 'metadata' && data.costo !== undefined) {
              return {
                name: data.obra,
                value: parseFloat(data.costo) || 0
              };
            }
            return null;
          })
          .filter(Boolean); // Remove any null entries

        console.log("Works:", works);

        const totalBudget = parseFloat(budgetInfo.amount);
        const usedBudget = works.reduce((sum, work) => sum + work.value, 0);
        const remainingBudget = totalBudget - usedBudget;

        setBudgetData({
          name: userArea,
          amount: totalBudget,
          works,
          usedBudget,
          remainingBudget
        });
        setError(null);
      } catch (error) {
        console.error("Error fetching budget data:", error);
        setError("Error al cargar los datos del presupuesto");
      }
    };

    fetchBudgetData();
  }, [userArea, reloadKey]);

  const handleReload = () => {
    setReloadKey(prevKey => prevKey + 1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          stroke: "grey",
          strokeWidth: "1px",
          paintOrder: "stroke"
        }}
      >
        {`${(percent * 100).toFixed(2)}%`}
      </text>
    );
  };

  if (error) return <div className="error-message">{error}</div>;
  if (!budgetData) return <div className="loading-message">Cargando gráficos...</div>;

  const chartData = [
    ...budgetData.works,
    { name: 'Presupuesto Restante', value: Math.max(budgetData.remainingBudget, 0) }
  ];

  return (
    <div className="budget-visualization" ref={containerRef}>
      <div className="budget-chart">
        <h3 className="budget-title">Distribución de Presupuesto</h3>
        <div className="chart-content">
          <div className="pie-container" style={{ width: '100%', height: chartSize.height }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={chartSize.width / 4}
                  innerRadius={chartSize.width / 6}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const item = payload[0].payload;
                      const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);
                      const percentage = (item.value / totalValue) * 100;
                      return (
                        <div className="custom-tooltip">
                          <p>{item.name}</p>
                          <p>{formatCurrency(item.value)} ({percentage.toFixed(2)}%)</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend 
                  formatter={(value, entry) => {
                    const item = chartData.find(d => d.name === value);
                    const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = (item.value / totalValue) * 100;
                    return `${value}: ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="budget-summary">
            <p className="total-budget">Presupuesto Total: {formatCurrency(budgetData.amount)}</p>
            <p className="used-budget">Presupuesto Utilizado: {formatCurrency(budgetData.usedBudget)}</p>
            <p className={`remaining-budget ${budgetData.remainingBudget < 0 ? 'exceeded' : ''}`}>
              Presupuesto Restante: {formatCurrency(Math.abs(budgetData.remainingBudget))}
              {budgetData.remainingBudget < 0 && (
                <span className="exceeded-warning"> (Presupuesto excedido)</span>
              )}
            </p>
          </div>
          <button 
            onClick={handleReload}
            className="btn btn-reload"
            aria-label="Recargar datos del gráfico"
          >
            Recargar Gráfico
          </button>
      </div>
    </div>
  );
};

export default SubareaBudgetVisualization;

